<template>
  <div class="col-12">
    <div class="card-body px-0 pb-2">
      <div class="p-4">
        <form @submit.prevent="handleSubmit" @keydown.enter.prevent>

          <!-- ACCOUNT -->
          <div class="section-header mb-3">
            <span class="section-icon"><i class="fas fa-user-circle"></i></span>
            <span>{{ $t('employees.sectionAccount') }}</span>
          </div>
          <div class="row g-3 mb-4">
            <div class="col-md-6">
              <label class="bmd-label-float">{{ $t('employees.username') }} <span class="text-danger">*</span></label>
              <input type="text" class="form-control borderless-input" :class="{ 'is-invalid': errors.username }"
                     v-model.trim="formData.username" required @blur="validateField('username')" />
              <div class="invalid-feedback">{{ errors.username }}</div>
            </div>

            <div class="col-md-6">
              <label class="bmd-label-float">{{ $t('employees.email') }}</label>
              <input type="email" class="form-control borderless-input" :class="{ 'is-invalid': errors.email }"
                     v-model.trim="formData.email" @blur="validateEmail()" />
              <div class="invalid-feedback">{{ errors.email }}</div>
            </div>

            <div class="col-md-6">
              <label class="bmd-label-float">
                {{ $t('employees.password') }}
                <span v-if="!isEditMode" class="text-danger">*</span>
              </label>
              <input type="password" class="form-control borderless-input" :class="{ 'is-invalid': errors.password }"
                     v-model="formData.password" :required="!isEditMode" @blur="validatePassword()" />
              <div class="invalid-feedback">{{ errors.password }}</div>
              <small v-if="isEditMode" class="text-muted">{{ $t('employees.passwordHint') }}</small>
            </div>

            <div class="col-md-6">
              <label class="bmd-label-float">
                {{ $t('employees.confirmPassword') }}
                <span v-if="!isEditMode" class="text-danger">*</span>
              </label>
              <input type="password" class="form-control borderless-input" :class="{ 'is-invalid': errors.password_confirmation }"
                     v-model="formData.password_confirmation" :required="!isEditMode" @blur="validateConfirmPassword()" />
              <div class="invalid-feedback">{{ errors.password_confirmation }}</div>
            </div>
          </div>

          <!-- PERSONAL -->
          <div class="section-header mb-3">
            <span class="section-icon"><i class="fas fa-id-card"></i></span>
            <span>{{ $t('employees.sectionPersonal') }}</span>
          </div>
          <div class="row g-3 mb-4">
            <div class="col-md-6">
              <label class="bmd-label-float">{{ $t('employees.gender') }} <span class="text-danger">*</span></label>
              <select class="form-select borderless-input" v-model="formData.gender" required>
                <option value="M">{{ $t('employees.male') }}</option>
                <option value="F">{{ $t('employees.female') }}</option>
              </select>
            </div>

            <div class="col-md-6">
              <label class="bmd-label-float">{{ $t('employees.nationality') }}</label>
              <select class="form-select borderless-input" v-model="formData.nationality_id" @change="handleNationalityChange">
                <option value="">{{ $t('employees.selectNationality') }}</option>
                <option v-for="nat in nationalities" :key="nat.id" :value="nat.id">
                  {{ localizedNatName(nat) }}
                </option>
              </select>
            </div>

            <div class="col-md-6">
              <label class="bmd-label-float">
                {{ $t('employees.nationalId') }}
                <span v-if="isLibyan" class="text-danger">*</span>
              </label>
              <input type="text" class="form-control borderless-input" :class="{ 'is-invalid': errors.national_num }"
                     v-model.trim="formData.national_num" :disabled="!formData.nationality_id" />
              <div class="invalid-feedback">{{ errors.national_num }}</div>
            </div>

            <div class="col-md-6">
              <label class="bmd-label-float">{{ $t('employees.passport') }}</label>
              <input type="text" class="form-control borderless-input"
                     v-model.trim="formData.passport_num" :disabled="isLibyan" />
            </div>

            <div class="col-md-6">
              <label class="bmd-label-float">{{ $t('employees.phone') }}</label>
              <input type="tel" class="form-control borderless-input"
                     v-model.trim="formData.phone_num" />
            </div>

            <div class="col-md-6">
              <label class="bmd-label-float">{{ $t('employees.address') }}</label>
              <input type="text" class="form-control borderless-input"
                     v-model.trim="formData.address" />
            </div>
          </div>

          <!-- PROFESSIONAL -->
          <div class="section-header mb-3">
            <span class="section-icon"><i class="fas fa-briefcase"></i></span>
            <span>{{ $t('employees.sectionProfessional') }}</span>
          </div>
          <div class="row g-3 mb-4">
            <div class="col-md-6">
              <label class="bmd-label-float">{{ $t('employees.jobTitle') }} <span class="text-danger">*</span></label>
              <select class="form-select borderless-input" :class="{ 'is-invalid': errors.job_title_id }"
                      v-model="formData.job_title_id" required>
                <option value="">—</option>
                <option v-for="jt in jobTitles" :key="jt.id" :value="jt.id">{{ jt.name }}</option>
              </select>
              <div class="invalid-feedback">{{ errors.job_title_id }}</div>
            </div>

            <div class="col-md-6">
              <label class="bmd-label-float">{{ $t('employees.qualification') }}</label>
              <select class="form-select borderless-input" v-model="formData.qualification">
                <option value="">—</option>
                <option v-for="qual in qualifications" :key="qual" :value="qual">{{ qual }}</option>
              </select>
            </div>

            <!-- Specialization: dropdown + custom text, same pattern as the teacher form -->
            <div class="col-md-6">
              <label class="bmd-label-float">{{ $t('employees.specialization') }}</label>
              <select class="form-select borderless-input" v-model="specializationPreset"
                      @change="onSpecializationPresetChange">
                <option value="">—</option>
                <option v-for="s in specializations" :key="s" :value="s">{{ s }}</option>
                <option value="_other">{{ $t('teachers.other') }}</option>
              </select>
            </div>
            <div class="col-md-6" v-if="specializationPreset === '_other'">
              <label class="bmd-label-float">{{ $t('teachers.specializationCustom') }}</label>
              <input type="text" class="form-control borderless-input"
                     v-model.trim="formData.specialization"
                     :placeholder="$t('teachers.specializationCustomPlaceholder')" />
            </div>

            <div class="col-md-6">
              <label class="bmd-label-float">{{ $t('employees.salary') }} <span class="text-danger">*</span></label>
              <div class="input-group">
                <input type="number" step="0.01" min="0" class="form-control borderless-input"
                       :class="{ 'is-invalid': errors.salary }"
                       v-model="formData.salary" required @blur="validateSalary()" />
                <span class="input-group-text">{{ $t('common.currency') }}</span>
              </div>
              <div class="invalid-feedback d-block">{{ errors.salary }}</div>
            </div>

            <div class="col-md-6">
              <label class="bmd-label-float">{{ $t('employees.hireDate') }}</label>
              <input type="date" class="form-control borderless-input" v-model="formData.hire_date" :max="maxDate" />
            </div>
          </div>

          <!-- HR -->
          <div class="section-header mb-3">
            <span class="section-icon"><i class="fas fa-folder-open"></i></span>
            <span>{{ $t('employees.hrInfo') }}</span>
          </div>
          <div class="row g-3 mb-4">
            <div class="col-md-6">
              <label class="bmd-label-float">{{ $t('employees.fileNumber') }}</label>
              <input type="text" class="form-control borderless-input" v-model.trim="formData.file_number" />
            </div>

            <div class="col-md-6">
              <label class="bmd-label-float">{{ $t('employees.clearanceDate') }}</label>
              <input type="date" class="form-control borderless-input" v-model="formData.clearance_date" />
            </div>

            <div class="col-md-6">
              <label class="bmd-label-float d-block mb-2">{{ $t('employees.isActive') }}</label>
              <div class="status-toggle">
                <button type="button"
                        class="status-btn"
                        :class="formData.is_active ? 'status-btn--active' : 'status-btn--inactive'"
                        @click="formData.is_active = !Boolean(formData.is_active)">
                  <i :class="formData.is_active ? 'fas fa-toggle-on' : 'fas fa-toggle-off'"></i>
                  <span>{{ formData.is_active ? $t('employees.active') : $t('employees.inactive') }}</span>
                </button>
              </div>
            </div>
          </div>

          <!-- BANKING -->
          <div class="section-header mb-3">
            <span class="section-icon"><i class="fas fa-university"></i></span>
            <span>{{ $t('employees.bankingInfo') }}</span>
          </div>
          <div class="row g-3 mb-4">
            <div class="col-md-6">
              <label class="bmd-label-float">{{ $t('employees.bankName') }}</label>
              <select class="form-select borderless-input" v-model="bankNamePreset"
                      @change="onBankPresetChange">
                <option value="">—</option>
                <option v-for="b in libyaBanks" :key="b" :value="b">{{ b }}</option>
                <option value="_other">{{ $t('teachers.other') }}</option>
              </select>
            </div>
            <div class="col-md-6" v-if="bankNamePreset === '_other'">
              <label class="bmd-label-float">{{ $t('teachers.bankNameCustom') }}</label>
              <input type="text" class="form-control borderless-input"
                     v-model.trim="formData.bank_name"
                     :placeholder="$t('teachers.bankNameCustomPlaceholder')" />
            </div>

            <div class="col-md-6">
              <label class="bmd-label-float">{{ $t('employees.bankBranch') }}</label>
              <input type="text" class="form-control borderless-input" v-model.trim="formData.bank_branch" />
            </div>

            <div class="col-md-6">
              <label class="bmd-label-float">{{ $t('employees.accountNumber') }}</label>
              <input type="text" class="form-control borderless-input" v-model.trim="formData.account_number" maxlength="15" />
            </div>

            <div class="col-md-6">
              <label class="bmd-label-float">{{ $t('employees.iban') }}</label>
              <input type="text" class="form-control borderless-input" v-model.trim="formData.iban" maxlength="23" />
            </div>

            <div class="col-md-6">
              <label class="bmd-label-float">{{ $t('employees.salaryPaymentMethod') }}</label>
              <select class="form-select borderless-input" v-model="formData.salary_payment_method">
                <option value="">—</option>
                <option value="cash">{{ $t('employees.salaryPaymentMethodCash') }}</option>
                <option value="certified_check">{{ $t('employees.salaryPaymentMethodCertifiedCheck') }}</option>
                <option value="bank_transfer">{{ $t('employees.salaryPaymentMethodBankTransfer') }}</option>
              </select>
            </div>
          </div>

          <!-- PHOTO -->
          <div class="section-header mb-3">
            <span class="section-icon"><i class="fas fa-camera"></i></span>
            <span>{{ $t('employees.profileImage') }}</span>
          </div>
          <div class="row g-3">
            <div class="col-12">
              <div class="photo-upload-area" @click="$refs.fileInput.click()">
                <input type="file" ref="fileInput" accept="image/*" style="display:none" @change="handleImageUpload" />
                <img v-if="previewImage" :src="previewImage" class="img-fluid mx-auto d-block" style="max-height:160px" />
                <div v-else class="photo-upload-placeholder">
                  <i class="fas fa-cloud-upload-alt fs-1 text-muted"></i>
                  <p class="text-muted mt-2 mb-0">{{ $t('employees.clickToUpload') }}</p>
                </div>
                <button v-if="previewImage" type="button" @click.stop="clearImage"
                        class="btn btn-sm btn-danger position-absolute top-0 end-0 m-2">
                  <i class="fas fa-trash"></i>
                </button>
              </div>
            </div>
          </div>

        </form>
      </div>
    </div>
  </div>
</template>

<style scoped>
.section-header {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.95rem;
  font-weight: 600;
  color: #344767;
  padding-bottom: 10px;
  border-bottom: 2px solid #e9ecef;
}
.section-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: linear-gradient(195deg, #2C91AE, #1E657A);
  box-shadow: 0 4px 6px -1px rgba(44, 145, 174, 0.35);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  flex-shrink: 0;
}
.photo-upload-area {
  position: relative;
  border: 2px dashed #dee2e6;
  border-radius: 12px;
  padding: 1.5rem;
  text-align: center;
  cursor: pointer;
  min-height: 130px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.photo-upload-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.status-toggle { display: flex; }
.status-btn {
  border: none;
  border-radius: 20px;
  padding: 7px 20px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.12);
}
.status-btn--active { background: linear-gradient(195deg, #4caf50, #3d8b40); color: #fff; }
.status-btn--inactive { background: linear-gradient(195deg, #ef5350, #c62828); color: #fff; }
</style>

<script setup>
import { ref, computed, onMounted, watch, defineEmits, defineProps, defineExpose, nextTick } from "vue";
import { useToast } from "vue-toastification";
import api from "@/api";
import { useI18n } from "vue-i18n";

const { t, locale } = useI18n();

const props = defineProps({
  isEditMode: { type: Boolean, default: false },
  initialData: {
    type: Object,
    default: () => ({
      username: '', email: '', password: '', password_confirmation: '',
      gender: 'M', nationality_id: '', national_num: '', passport_num: '',
      phone_num: '', address: '', qualification: '', specialization: '',
      job_title_id: '', salary: '', hire_date: '', image: null,
      file_number: '', clearance_date: '', is_active: true,
      bank_name: '', bank_branch: '', account_number: '', iban: '', salary_payment_method: '',
    })
  }
});

const emit = defineEmits(['submit', 'cancel', 'validation-error', 'update:form-data']);
const toast = useToast();

const formData = ref({ ...props.initialData });
const errors = ref({});
const fileInput = ref(null);
const previewImage = ref(props.initialData.image_url || null);
const nationalities = ref([]);
const jobTitles = ref([]);

// Same fixed lists/pattern as TeacherForm.vue, so employee and teacher forms
// behave identically for these two fields.
const qualifications = ref([
  t('teachers.qualifications.bachelor'),
  t('teachers.qualifications.master'),
  t('teachers.qualifications.phd'),
  t('teachers.qualifications.diploma'),
  t('teachers.qualifications.highSchool'),
]);

const specializations = [
  'اللغة العربية',
  'اللغة الإنجليزية',
  'اللغة الفرنسية',
  'الرياضيات',
  'الفيزياء',
  'الكيمياء',
  'الأحياء',
  'التاريخ',
  'الجغرافيا',
  'الحاسوب',
  'التربية الفنية',
  'التربية البدنية',
  'التربية الإسلامية',
  'التربية الخاصة',
  'الإدارة والاقتصاد',
];

const specializationPreset = ref('');

const onSpecializationPresetChange = (e) => {
  const val = e.target.value;
  if (val !== '_other') formData.value.specialization = val;
  else formData.value.specialization = '';
};

// Same bank list/pattern as TeacherForm.vue, so employee and teacher forms
// use the same dropdown instead of employees typing the bank name freehand.
const libyaBanks = [
  'مصرف الجمهورية',
  'المصرف التجاري الوطني',
  'مصرف الوحدة',
  'مصرف الأمة',
  'مصرف الشمال الأفريقي',
  'مصرف الواحة',
  'مصرف الاستثمار والتنمية الريفية',
  'مصرف ليبيا الخارجي',
  'مصرف الخليج التجاري',
  'مصرف المتوسط',
  'مصرف السراي',
  'مصرف الإسكان والتعمير',
  'مصرف برقة',
  'مصرف التجارة والتنمية',
  'مصرف ليبيا المركزي',
];

const bankNamePreset = ref('');

const onBankPresetChange = (e) => {
  const val = e.target.value;
  if (val !== '_other') formData.value.bank_name = val;
  else formData.value.bank_name = '';
};

const isLibyan = computed(() => {
  if (!formData.value.nationality_id || nationalities.value.length === 0) return false;
  const selected = nationalities.value.find(n => n.id.toString() === formData.value.nationality_id.toString());
  return selected?.code === "LY";
});

const maxDate = computed(() => new Date().toISOString().split('T')[0]);

const localizedNatName = (nat) => {
  if (!nat) return '';
  const l = (locale.value || 'en').split('-')[0];
  return nat.translations?.[l] ?? nat.translations?.en ?? nat.name ?? '';
};

const validateField = (field) => {
  if (!formData.value[field]) {
    errors.value[field] = t('validation.required');
    return false;
  }
  delete errors.value[field];
  return true;
};

const validateEmail = () => {
  if (formData.value.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.value.email)) {
    errors.value.email = t('validation.invalidEmail');
    return false;
  }
  delete errors.value.email;
  return true;
};

const validatePassword = () => {
  if (!props.isEditMode && !formData.value.password) {
    errors.value.password = t('validation.required');
    return false;
  }
  if (formData.value.password && formData.value.password.length < 8) {
    errors.value.password = t('validation.passwordMinLength');
    return false;
  }
  delete errors.value.password;
  return true;
};

const validateConfirmPassword = () => {
  if (formData.value.password !== formData.value.password_confirmation) {
    errors.value.password_confirmation = t('validation.passwordMismatch');
    return false;
  }
  delete errors.value.password_confirmation;
  return true;
};

const validateSalary = () => {
  if (formData.value.salary === '' || formData.value.salary === null) {
    errors.value.salary = t('validation.required');
    return false;
  }
  if (formData.value.salary < 0) {
    errors.value.salary = t('validation.salaryPositive');
    return false;
  }
  delete errors.value.salary;
  return true;
};

const fetchNationalities = async () => {
  try {
    const response = await api.get('/nationalities', { params: { locale: (locale.value || 'ar').split('-')[0] } });
    nationalities.value = response.data.data;
  } catch (e) {
    nationalities.value = [];
  }
};

const fetchJobTitles = async () => {
  try {
    const response = await api.get('/job-titles', { params: { category: 'employee', all: 1 } });
    jobTitles.value = response.data.data || response.data || [];
  } catch (e) {
    jobTitles.value = [];
  }
};

const handleNationalityChange = () => {
  if (!isLibyan.value) formData.value.national_num = "";
  else formData.value.passport_num = "";
};

const handleImageUpload = (event) => {
  const file = event.target.files[0];
  if (!file) { clearImage(); return; }

  const validTypes = ["image/jpeg", "image/png", "image/jpg", "image/gif"];
  if (!validTypes.includes(file.type.toLowerCase()) || file.size > 2 * 1024 * 1024) {
    toast.error(t('validation.imageTypeSize'));
    clearImage();
    return;
  }

  formData.value.image = file;
  const reader = new FileReader();
  reader.onload = (e) => { previewImage.value = e.target.result; };
  reader.readAsDataURL(file);
};

const clearImage = () => {
  formData.value.image = null;
  previewImage.value = null;
  if (fileInput.value) fileInput.value.value = "";
};

const validateForm = () => {
  errors.value = {};
  const validations = [
    validateField('username'),
    validateEmail(),
    validateField('gender'),
    validateField('job_title_id'),
    validateSalary(),
  ];

  if (!props.isEditMode) {
    validations.push(validatePassword());
    validations.push(validateConfirmPassword());
  }

  return validations.every(v => v);
};

const setBackendErrors = (backendErrors) => {
  errors.value = {};
  for (const field in backendErrors) {
    if (field in formData.value) errors.value[field] = backendErrors[field][0];
  }
  const firstErrorField = Object.keys(backendErrors)[0];
  nextTick(() => {
    const input = document.getElementById(firstErrorField);
    if (input) input.focus();
  });
};

const handleSubmit = async () => {
  try {
    if (!validateForm()) {
      emit("validation-error");
      toast.warning(t('validation.correctErrors'));
      return;
    }

    const formDataToSend = new FormData();
    Object.keys(formData.value).forEach((key) => {
      if (key === 'is_active') {
        formDataToSend.append('is_active', formData.value.is_active ? 1 : 0);
      } else if (formData.value[key] !== null && formData.value[key] !== undefined && formData.value[key] !== '') {
        formDataToSend.append(key, formData.value[key]);
      }
    });

    if (props.isEditMode) {
      formDataToSend.append("_method", "PUT");
    }

    await emit("submit", formDataToSend);
  } catch (error) {
    if (error.response?.status === 422) {
      setBackendErrors(error.response.data.errors);
      const firstError = Object.values(error.response.data.errors)[0][0];
      toast.error(firstError);
    } else {
      toast.error(error.response?.data?.message || t('validation.submissionError'));
    }
  }
};

const submitForm = () => { handleSubmit(); };
defineExpose({ validateForm, submitForm, setBackendErrors });

onMounted(() => {
  fetchNationalities();
  fetchJobTitles();
});

watch(() => props.initialData, (newData) => {
  const rawActive = newData.is_active;
  const isActive = rawActive == null ? true : (rawActive !== false && rawActive !== 0 && rawActive !== "0");

  formData.value = {
    ...newData,
    password: '',
    password_confirmation: '',
    image: formData.value.image,
    image_url: newData.image_url || formData.value.image_url,
    is_active: isActive,
  };
  previewImage.value = newData.image_url || null;

  // Sync specialization preset (dropdown vs. free-text "other")
  const sv = newData.specialization || '';
  specializationPreset.value = sv === '' ? '' : (specializations.includes(sv) ? sv : '_other');

  // Sync bank preset (dropdown vs. free-text "other")
  const bv = newData.bank_name || '';
  bankNamePreset.value = bv === '' ? '' : (libyaBanks.includes(bv) ? bv : '_other');
}, { immediate: true, deep: true });

watch(formData, (newVal) => {
  emit('update:form-data', newVal);
}, { deep: true });
</script>
